import { Injectable, Injector, NgZone } from "@angular/core";
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
} from "@angular/router";
import { Device } from "@awesome-cordova-plugins/device/ngx";
import { VERSION } from "../../../environments/version";
import { Api } from "../../api/base";
import { ClientLogProvider } from "../../api/client-log/client-log.api";
import { AppConfig } from "../../app.config";
import { stripQueryString } from "./client-diagnostics";
import {
  addStartupMark,
  buildStartupDomSnapshot,
  buildStartupNetworkTiming,
  buildStartupPhases,
  createStartupTrace,
  documentProtocol,
  formatStartupMarks,
  isRootPage,
  lastStartupMarkName,
  loadStartupTrace,
  parseEpoch,
  saveStartupTrace,
  shouldReportSlow,
  STARTUP_HTML_BOOT_KEY,
  STARTUP_HTML_ORPHAN_KEY,
  STARTUP_HTML_RESOURCE_ERRORS_KEY,
  StartupTrace,
  wasNeverVisible,
} from "./startup-trace";

// 画面が表示されている状態でこの時間たっても最初の画面が出なければ「起動が止まった」とみなす
const STALL_THRESHOLD_MS = 15000;
// ページの読み込み開始から最初の画面までこれ以上かかったら「起動が遅かった」として記録する。
// 「放置すると数分後に開く」型の白画面は Angular 起動前(HTML/JS の読み込み)で長引くことがあり、
// STALL_THRESHOLD_MS(Angular 起動後の時間)では拾えないため
const SLOW_THRESHOLD_MS = 20000;
// 起動中に裏へ回った場合、表示されていた時間がこれ未満なら遅かった起動として扱わない
const SLOW_MIN_VISIBLE_MS_IF_HIDDEN = 5000;
const STALL_CHECK_INTERVAL_MS = 1000;
// 前回分の送信はヘッダー(OSバージョン等)の準備を待つが、準備自体が止まった場合に備えて上限を設ける
const PREVIOUS_REPORT_FALLBACK_MS = 10000;
const MAX_URL_LENGTH = 40;

type PreviousReport = {
  trace: StartupTrace | null;
  /** 前回の HTML 読み込みが Angular の起動に届かなかった場合、その時刻 */
  orphanHtmlBootAt: number | null;
  resourceErrors: string;
};

/**
 * 起動の節目を記録し、最初の画面が表示されないまま止まった起動を ClientErrorLog に送る。
 * 送信内容は controller_log(BigQuery)の params にも残るため、user_id で個別に追跡できる。
 * 詳細は startup-trace.ts 冒頭のコメント参照。
 */
@Injectable({ providedIn: "root" })
export class StartupTraceService {
  private trace: StartupTrace | null = null;
  private previous: PreviousReport | null = null;
  private stallTimer: ReturnType<typeof setInterval> | null = null;
  private visibleSince: number | null = null;

  constructor(
    private injector: Injector,
    private router: Router,
    private zone: NgZone
  ) {}

  /** AppComponent の constructor の先頭で1回だけ呼ぶ */
  begin(): void {
    if (this.trace) {
      return;
    }
    try {
      const now = Date.now();
      const htmlBootAt = parseEpoch(
        localStorage.getItem(STARTUP_HTML_BOOT_KEY)
      );
      const orphanHtmlBootAt = parseEpoch(
        localStorage.getItem(STARTUP_HTML_ORPHAN_KEY)
      );
      const resourceErrors =
        localStorage.getItem(STARTUP_HTML_RESOURCE_ERRORS_KEY) ?? "";
      // Angular まで起動できたので、index.html 側の記録は消費済みにする
      localStorage.removeItem(STARTUP_HTML_BOOT_KEY);
      localStorage.removeItem(STARTUP_HTML_ORPHAN_KEY);
      localStorage.removeItem(STARTUP_HTML_RESOURCE_ERRORS_KEY);

      const prev = loadStartupTrace(localStorage);
      const prevIncomplete =
        !!prev && !prev.ready && !prev.reported && !wasNeverVisible(prev);
      if (prevIncomplete || orphanHtmlBootAt) {
        this.previous = {
          trace: prevIncomplete ? prev : null,
          orphanHtmlBootAt,
          resourceErrors,
        };
        setTimeout(
          () => this.flushPreviousReport(),
          PREVIOUS_REPORT_FALLBACK_MS
        );
      }

      const navStartAt =
        typeof performance !== "undefined" && performance.timeOrigin > 0
          ? Math.round(performance.timeOrigin)
          : null;
      this.trace = createStartupTrace(
        now,
        htmlBootAt,
        VERSION.hash || "",
        navStartAt,
        typeof performance !== "undefined"
          ? documentProtocol(performance)
          : null
      );
      this.visibleSince = document.visibilityState === "visible" ? now : null;
      addStartupMark(this.trace, `app_start:${document.visibilityState}`, now);
      this.save();
      // 観察するだけなので、起動中に毎秒の変更検知を走らせないよう Angular の外で動かす
      this.zone.runOutsideAngular(() => this.listen());
    } catch {
      // 診断の失敗で起動を妨げない
    }
  }

  /** 起動の節目を記録する。最初の画面の表示後は何もしない */
  mark(name: string): void {
    if (!this.trace || this.trace.ready) {
      return;
    }
    addStartupMark(this.trace, name, Date.now());
    this.save();
  }

  /** 認証できたユーザーを覚えておく(認証前に止まった起動との区別、前回分の送信時の user_id) */
  setUserId(userId: number): void {
    if (!this.trace) {
      return;
    }
    this.trace.userId = userId;
    this.mark("auth_success");
  }

  /** API ヘッダー(OS・アプリのバージョン等)の準備ができた。前回分はヘッダー付きで送る */
  headerReady(): void {
    this.mark("header_ready");
    this.flushPreviousReport();
  }

  private listen(): void {
    // ライフサイクルイベントはバブリングしないため capture で拾う
    document.addEventListener(
      "ionViewDidEnter",
      (event) => this.onViewDidEnter(event.target as Element | null),
      true
    );
    document.addEventListener("visibilitychange", () =>
      this.onVisibilityChange()
    );
    window.addEventListener("pagehide", () => this.mark("pagehide"));
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        this.mark(`nav_start:${this.shortUrl(event.url)}`);
      } else if (event instanceof NavigationEnd) {
        this.mark(`nav_end:${this.shortUrl(event.urlAfterRedirects)}`);
      } else if (event instanceof NavigationCancel) {
        this.mark(`nav_cancel:${this.shortUrl(event.url)}`);
      } else if (event instanceof NavigationError) {
        // lazy chunk の読み込み失敗はここに来る
        this.mark(
          `nav_error:${stripQueryString(String(event.error?.message ?? event.error))}`
        );
      }
    });
    this.stallTimer = setInterval(
      () => this.checkStall(),
      STALL_CHECK_INTERVAL_MS
    );
  }

  private onViewDidEnter(el: Element | null): void {
    if (!this.trace || this.trace.ready || !el) {
      return;
    }
    const tag = el.tagName.toLowerCase();
    if (!isRootPage(el)) {
      this.mark(`enter:${tag}`);
      return;
    }
    this.mark(`ready:${tag}`);
    const now = Date.now();
    this.accumulateVisible(now);
    this.trace.ready = true;
    this.save();
    this.stopStallCheck();
    this.reportIfSlow(this.trace, now);
  }

  /** 最初の画面は出たが、読み込み開始から時間がかかりすぎた起動を記録する */
  private reportIfSlow(trace: StartupTrace, now: number): void {
    const origin = trace.navStartAt ?? trace.htmlBootAt ?? trace.startedAt;
    const totalMs = now - origin;
    if (
      !shouldReportSlow(
        trace,
        totalMs,
        SLOW_THRESHOLD_MS,
        SLOW_MIN_VISIBLE_MS_IF_HIDDEN
      )
    ) {
      return;
    }
    this.send("startup_slow", trace, {
      total_ms: totalMs,
      net: buildStartupNetworkTiming(performance, location.host),
    });
  }

  private onVisibilityChange(): void {
    const now = Date.now();
    if (document.visibilityState === "visible") {
      this.visibleSince = now;
    } else {
      this.accumulateVisible(now);
      this.visibleSince = null;
    }
    this.mark(`vis:${document.visibilityState}`);
  }

  private accumulateVisible(now: number): void {
    if (this.trace && this.visibleSince !== null) {
      this.trace.visibleMs += now - this.visibleSince;
      this.visibleSince = now;
    }
  }

  private checkStall(): void {
    const trace = this.trace;
    if (!trace || trace.ready) {
      this.stopStallCheck();
      return;
    }
    const now = Date.now();
    this.accumulateVisible(now);
    trace.aliveMs = now - trace.startedAt;
    // 次回起動時の報告(前回分)でも表示時間と生存時刻が分かるよう毎回保存する
    this.save();
    if (trace.visibleMs < STALL_THRESHOLD_MS) {
      return;
    }
    this.stopStallCheck();
    trace.reported = true;
    this.save();
    this.send("startup_stalled", trace, {
      dom: buildStartupDomSnapshot(document),
      net: buildStartupNetworkTiming(performance, location.host),
    });
  }

  private flushPreviousReport(): void {
    const previous = this.previous;
    if (!previous) {
      return;
    }
    this.previous = null;
    const prev = previous.trace;
    this.send(
      "startup_incomplete_previous",
      prev,
      {
        html_orphan_at: previous.orphanHtmlBootAt,
        res_errors: previous.resourceErrors || undefined,
      },
      prev ? undefined : "html_only"
    );
  }

  private send(
    kind: "startup_stalled" | "startup_slow" | "startup_incomplete_previous",
    trace: StartupTrace | null,
    extra: { [key: string]: unknown },
    lastMarkOverride?: string
  ): void {
    try {
      const lastMark =
        lastMarkOverride ?? (trace ? lastStartupMarkName(trace) : "none");
      const api = this.injector.get(Api);
      const context = {
        kind,
        started_at: trace?.startedAt,
        // HTML が届くまで / HTML から Angular 起動まで(バンドルの読み込み時間)
        ...(trace ? buildStartupPhases(trace) : {}),
        visible_ms: trace?.visibleMs,
        alive_ms: trace?.aliveMs,
        doc_proto: trace?.docProtocol,
        trace_web_build: trace?.webBuild,
        // 以下は送信時点(=今回の起動)の値。UA の OS バージョンは iOS 26 で凍結されるため実値を送る
        web_build: VERSION.hash || "",
        app_version: api.getHeader("X-Ckuru-App-Version"),
        os_version: api.getHeader("X-Ckuru-Os-Version"),
        // X-Ckuru-Model は UA 由来で iOS だと "iPhone" にしかならないため、ネイティブなら実機種(例: iPhone15,2)を送る
        model: this.nativeModel() ?? api.getHeader("X-Ckuru-Model"),
        ...extra,
      };
      const message = `${kind}: ${lastMark}`;
      if (AppConfig.ENV !== "production") {
        console.warn("[startup-trace]", message, trace, context);
        return;
      }
      this.injector.get(ClientLogProvider).post({
        user_id: trace?.userId ?? this.trace?.userId ?? "",
        message,
        url: stripQueryString(document.location?.hash ?? ""),
        path: stripQueryString(document.URL),
        stack: trace ? formatStartupMarks(trace.marks) : "",
        context: JSON.stringify(context),
      });
    } catch {
      // 診断の失敗で画面の動作を妨げない
    }
  }

  private nativeModel(): string | null {
    try {
      if (AppConfig.IS_BROWSER) {
        return null;
      }
      return this.injector.get(Device).model || null;
    } catch {
      return null;
    }
  }

  private stopStallCheck(): void {
    if (this.stallTimer) {
      clearInterval(this.stallTimer);
      this.stallTimer = null;
    }
  }

  private shortUrl(url: string): string {
    return stripQueryString(url).slice(0, MAX_URL_LENGTH);
  }

  private save(): void {
    if (this.trace) {
      saveStartupTrace(localStorage, this.trace);
    }
  }
}
